<template>
  <!-- Newsletter Wrapper -->
  <div class="w-full">
    <div v-if="config.build" class="w-full space-y-2 pb-6">
      <div class="flex justify-between">
        <div class="flex flex-col space-y-2">
          <button
            type="button"
            :class="[
              publishWithoutEmail ? 'bg-primary' : 'bg-gray-200',
              toggleClass,
            ]"
            role="switch"
            aria-checked="false"
            @click="publishWithoutEmail = !publishWithoutEmail"
          >
            <span
              aria-hidden="true"
              class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-all ring-0 ease-in-out duration-200"
              :class="[publishWithoutEmail ? 'translate-x-5' : 'translate-x-0']"
            ></span>
          </button>
          <span class="text-truegray-500 text-xs relative flex pr-2 group">
            Publish without emailing
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="ml-2 w-4 h-4"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.94 6.94a.75.75 0 11-1.061-1.061 3 3 0 112.871 5.026v.345a.75.75 0 01-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 108.94 6.94zM10 15a1 1 0 100-2 1 1 0 000 2z"
                clip-rule="evenodd"
              />
            </svg>
            <div
              class="absolute left-full top-0 w-64 z-50 border border-truegray-300 rounded p-4 opacity-0 transition-all duration-300 transform pointer-events-none shadow group-hover:opacity-100 bg-white"
            >
              Use this option if you would like to publish a newsletter without
              sending it to your subscribers. This allows you to copy the link,
              and view the newsletter within your settings.
            </div>
          </span>
        </div>

        <!-- Send button for publish, seperate button used below for all other submit types -->
        <div v-if="publishWithoutEmail" class="">
          <button
            :disabled="!canSubmit"
            class="btn btn-primary h-12 relative"
            :class="{
              'opacity-25 cursor-not-allowed': !canSubmit,
            }"
            @click="onSubmit"
          >
            <div
              v-if="submitting"
              class="absolute inset-0 w-full flex justify-center items-center z-20"
            >
              <lottie :options="loadingAnimationOptions" />
            </div>
            <span v-else> Publish </span>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="!newsletterSubjectUpdate && config.build && !publishWithoutEmail"
      class="w-full mb-2"
    >
      <p class="text-xs text-truegray-300 italic">
        This is a preview of how the newsletter will appear in your inbox
      </p>
    </div>
    <!-- Inbox/subject -->
    <div
      v-if="!publishWithoutEmail && config.build"
      :class="!!queuedMailLists.length ? 'mb-8' : ''"
    >
      <div class="flex items-center mb-8">
        <!-- Content -->
        <div
          :class="[
            !newsletterSubjectUpdate && !newsletterSubject.length > 0
              ? 'border border-truegray-300'
              : '',
            !newsletterSubjectUpdate && newsletterSubject.length > 0
              ? 'border-2 border-green-400'
              : '',
            !newsletterSubjectUpdate ? ' p-3' : '',
          ]"
          class="w-full rounded-xl"
        >
          <!-- Output -->
          <div v-if="!newsletterSubjectUpdate" class="flex items-center">
            <!-- Icon -->
            <div
              :class="
                newsletterSubject.length > 0
                  ? 'text-green-400'
                  : 'text-truegray-300'
              "
              class="mr-6 transition-all"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                class="w-5 h-5"
              >
                <path
                  d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z"
                />
                <path
                  d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z"
                />
              </svg>
            </div>
            <!-- Content -->
            <div class="w-full">
              <p class="">
                {{ settings.profile_title }}
              </p>
              <div class="flex items-center mt-2">
                <div class="m-0 text-xs w-full">
                  <p v-if="newsletterSubject.length > 0" class="clamp-subject">
                    > {{ newsletterSubject }}
                  </p>
                  <p v-else class="flex items-center text-xs text-red-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-4 h-4 mr-2"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Enter your email subject
                  </p>
                </div>
                <p
                  v-if="dateDisplayed"
                  class="text-truegray-400 text-xs m-0 ml-auto flex-shrink-0"
                >
                  {{ dateDisplayed }}
                </p>
              </div>
            </div>

            <div
              class="w-12 h-10 pl-2 ml-4 flex items-center justify-center border-l border-truegray-300 text-black hover:text-primary cursor-pointer"
              @click="newsletterSubjectUpdate = !newsletterSubjectUpdate"
            >
              <svg
                v-if="!newsletterSubjectUpdate"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-3 h-3"
              >
                <path
                  d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32L19.513 8.2z"
                />
              </svg>
            </div>
          </div>

          <!-- Input -->
          <div v-else :class="inputWrapperClass" class="h-12">
            <h6 class="flex items-center group" :class="inputLabelClass">
              Newsletter Subject
            </h6>
            <input
              id="newsletter_subject"
              v-model="newsletterSubject"
              type="text"
              name="newsletter_subject"
              autocomplete="newsletter_subject"
              :class="inputTextClass"
              class="m-0"
            />
          </div>
        </div>
        <!-- Edit/save -->
        <div
          v-if="newsletterSubjectUpdate"
          class="ml-4 cursor-pointer opacity-50 hover:opacity-100 transition-all"
          :class="newsletterSubjectUpdate ? 'text-primary' : ''"
          @click="newsletterSubjectUpdate = !newsletterSubjectUpdate"
        >
          <svg
            v-if="!newsletterSubjectUpdate"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="w-3 h-3"
          >
            <path
              d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32L19.513 8.2z"
            />
          </svg>

          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="w-5 h-5"
          >
            <path
              fill-rule="evenodd"
              d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </div>

      <!-- Send/schedule -->
      <div class="flex relative z-10">
        <NewsletterListDropdown
          :label="'Send to'"
          :response-selection-id="'newQueuedMailLists'"
          :height="'12'"
        />
        <button
          class="flex items-center justify-center w-12 h-12 border-truegray-300 border-t border-b flex-shrink-0 transition-all hover:text-white hover:bg-black"
          @click="openSendingOptions"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="w-3 h-3"
          >
            <path
              fill-rule="evenodd"
              d="M8.34 1.804A1 1 0 019.32 1h1.36a1 1 0 01.98.804l.295 1.473c.497.144.971.342 1.416.587l1.25-.834a1 1 0 011.262.125l.962.962a1 1 0 01.125 1.262l-.834 1.25c.245.445.443.919.587 1.416l1.473.294a1 1 0 01.804.98v1.361a1 1 0 01-.804.98l-1.473.295a6.95 6.95 0 01-.587 1.416l.834 1.25a1 1 0 01-.125 1.262l-.962.962a1 1 0 01-1.262.125l-1.25-.834a6.953 6.953 0 01-1.416.587l-.294 1.473a1 1 0 01-.98.804H9.32a1 1 0 01-.98-.804l-.295-1.473a6.957 6.957 0 01-1.416-.587l-1.25.834a1 1 0 01-1.262-.125l-.962-.962a1 1 0 01-.125-1.262l.834-1.25a6.957 6.957 0 01-.587-1.416l-1.473-.294A1 1 0 011 10.68V9.32a1 1 0 01.804-.98l1.473-.295c.144-.497.342-.971.587-1.416l-.834-1.25a1 1 0 01.125-1.262l.962-.962A1 1 0 015.38 3.03l1.25.834a6.957 6.957 0 011.416-.587l.294-1.473zM13 10a3 3 0 11-6 0 3 3 0 016 0z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
        <button
          :disabled="!canSubmit"
          class="btn btn-primary h-12 rounded-none rounded-r-xl relative"
          :class="{
            'opacity-25 cursor-not-allowed': !canSubmit,
          }"
          @click="onSubmit"
        >
          <div
            v-if="submitting"
            class="absolute inset-0 w-full flex justify-center items-center z-20"
          >
            <lottie :options="loadingAnimationOptions" />
          </div>
          <span v-else>
            {{ submitText }}
          </span>
        </button>

        <div
          v-if="optionsOpen"
          :style="{ top: '70%' }"
          class="absolute right-0 w-3/4 xl:w-1/2 py-4 px-6 2xl:p-8 border border-truegray-300 rounded bg-white z-20"
        >
          <div class="relative mb-6">
            <h3 class="text-lg flex items-center space-x-2">
              Options
              <ProTag
                :class="isPro ? '' : 'cursor-pointer'"
                class="ml-3"
                :config="proTagConfig"
              />
            </h3>
            <button
              type="button"
              class="absolute right-0 top-0 transform hover:rotate-180 transition-all duration-300 hover:text-primary"
              @click="closeSchedule"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div :class="[isPro ? '' : 'cursor-not-allowed']">
            <div :class="[isPro ? '' : 'pointer-events-none']">
              <div
                :class="[isPro ? '' : 'cursor-not-allowed']"
                class="relative mb-6 pb-6 border-b border-truegray-300"
              >
                <div class="flex">
                  <h3 class="text-sm mb-2">Schedule</h3>
                </div>
                <h6 class="text-truegray-700">Date/Time</h6>
                <div :class="[isPro ? '' : 'pointer-events-none']" class="flex">
                  <date-picker
                    v-model="datetimeScheduled"
                    type="datetime"
                    :disabled-date="disabledDate"
                    :disabled-time="disabledTime"
                    :show-second="false"
                    :use12h="false"
                    :minute-step="15"
                    value-type="format"
                  ></date-picker>
                  <button
                    type="button"
                    class="ml-4 transform hover:rotate-180 transition-all duration-300 hover:text-primary"
                    @click="resetSchedule"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="w-6 h-6 hover:text-primary"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div
                :class="[isPro ? '' : 'pointer-events-none']"
                class="w-full flex justify-between items-center space-x-3 pb-6"
              >
                <span>Save as Draft</span>
                <button
                  type="button"
                  :class="[
                    saveAsDraft ? 'bg-primary' : 'bg-gray-200',
                    toggleClass,
                  ]"
                  role="switch"
                  aria-checked="false"
                  @click="saveAsDraft = !saveAsDraft"
                >
                  <span
                    aria-hidden="true"
                    class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-all ring-0 ease-in-out duration-200"
                    :class="[saveAsDraft ? 'translate-x-5' : 'translate-x-0']"
                  ></span>
                </button>
              </div>

              <button
                :class="[isPro ? '' : 'pointer-events-none']"
                class="btn btn-primary mb-0 h-8 px-6"
                @click="closeSchedule"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Builder/preview -->
    <div class="w-full p-2 lg:p-12 2xl:p-24 bg-truegray-300">
      <div
        class="newsletter-preview shadow pb-6 rounded-xl overflow-hidden bg-white"
      >
        <!-- primary banner -->
        <div
          class="w-full bg-primary flex items-center justify-center py-3 space-x-4 text-white"
        >
          <div
            :style="{ padding: '3px' }"
            class="rounded-full bg-white overflow-hidden"
          >
            <img class="w-auto h-8" :src="settings.logo" alt="School Logo" />
          </div>
          <p :style="{ fontSize: '14px' }">
            {{ settings.profile_title }}
          </p>
        </div>

        <!-- content wrapper -->
        <div class="mx-6">
          <!-- title -->
          <div class="px-2 py-8 lg:px-8 flex items-center justify-center">
            <!-- Output -->
            <h1
              v-if="newsletterTitle.length > 0 && !newsletterTitleUpdate"
              class="newsletter-title m-0 text-center"
            >
              {{ newsletterTitle }}
            </h1>
            <!-- Placeholder -->
            <div v-if="config.build">
              <p
                v-if="!newsletterTitleUpdate && !newsletterTitle.length > 0"
                class="text-xs text-center mb-2 font-normal italic"
              >
                To get started...
              </p>
              <h1
                v-if="!newsletterTitle.length > 0 && !newsletterTitleUpdate"
                class="newsletter-title m-0 text-center"
              >
                Add a title
              </h1>
            </div>
            <!-- Input -->
            <div
              v-if="newsletterTitleUpdate && config.build"
              :class="inputWrapperClass"
              class="h-12"
            >
              <h6 class="flex items-center group" :class="inputLabelClass">
                Newsletter Title
              </h6>
              <input
                id="newsletter_title"
                v-model="newsletterTitle"
                type="text"
                name="newsletter_title"
                autocomplete="newsletter_title"
                :class="inputTextClass"
                class="m-0"
              />
            </div>

            <!-- Edit/Save -->
            <div
              v-if="config.build"
              class="ml-4 cursor-pointer opacity-50 hover:opacity-100 transition-all"
              :class="newsletterTitleUpdate ? 'text-primary' : ''"
              @click="newsletterTitleUpdate = !newsletterTitleUpdate"
            >
              <svg
                v-if="!newsletterTitleUpdate"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-3 h-3"
              >
                <path
                  d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32L19.513 8.2z"
                />
              </svg>

              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                class="w-5 h-5"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>

          <!-- Blurb -->
          <div
            v-if="newsletterBlurb.length > 0 && !newsletterBlurbUpdate"
            class="bg-truegray-100 w-full px-6 py-4 mb-4"
          >
            <p class="newsletter-body-text">
              {{ newsletterBlurb }}
            </p>
          </div>

          <!-- Prompts -->
          <div
            v-if="
              newsletterTitle.length > 0 &&
              !selectedPosts.length > 0 &&
              !newsletterTitleUpdate &&
              !newsletterBlurbUpdate &&
              config.build
            "
            class="pb-4 w-full text-center"
          >
            <p>
              <span class="text-green-500 font-bold">Great!</span>
              <span>
                {{ postsPromptText }}
              </span>
            </p>
          </div>

          <!-- Newsletter blurb -->
          <div
            v-if="config.build"
            class="transition-all duration-300 ease-in-out flex items-center justify-center"
            :class="[
              newsletterBlurbUpdate
                ? 'mt-6 mb-4'
                : 'h-0 overflow-hidden border-none',
              'rounded relative bg-white w-full', // not using data as I couldnt overwrite the shadow/border class
            ]"
          >
            <h6
              class="absolute transform border-t border-truegray-300 duration-100 ease-linear pt-2 pb-1 px-1 left-3 top-0 z-40 text-truegray-700 bg-white"
            >
              Newsletter blurb
              <span
                v-if="blurbLength"
                :class="blurbLength > 490 ? 'text-red-400' : ''"
                >- {{ blurbLength }}/500 characters</span
              >
            </h6>
            <textarea
              id="newsletterBlurb"
              v-model="newsletterBlurb"
              name="newsletterBlurb"
              rows="2"
              :class="[inputTextAreaClass, 'border border-truegray-300 pt-6']"
              @keydown="validateBlurb"
              @change="validateBlurb"
            ></textarea>
            <div
              v-if="config.build"
              class="ml-4 cursor-pointer opacity-50 hover:opacity-100 transition-all text-primary"
              @click="newsletterBlurbUpdate = !newsletterBlurbUpdate"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                class="w-5 h-5"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>

          <!-- Add posts/blurb -->
          <div
            v-if="
              !newsletterBlurbUpdate &&
              !newsletterTitleUpdate &&
              newsletterTitle.length > 0 &&
              config.build
            "
            class="flex space-x-4 items-center justify-center"
            :class="!selectedPosts.length > 0 ? 'mb-12' : ''"
          >
            <button
              class="m-0 btn btn-trans-black flex items-center justify-center h-6 px-3"
              :class="[
                posts.length >= postsPerNewsletter
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:border-b',
              ]"
              :disabled="posts.length >= postsPerNewsletter"
              @click="openPostModal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3 w-3 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <span :style="{ fontSize: '0.75rem' }">{{
                postsButtonText
              }}</span>
            </button>

            <button
              class="m-0 btn btn-black flex items-center justify-center h-6 px-3"
              :class="[
                posts.length >= postsPerNewsletter
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:border-b',
              ]"
              :disabled="posts.length >= postsPerNewsletter"
              @click="newsletterBlurbUpdate = !newsletterBlurbUpdate"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3 w-3 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <span :style="{ fontSize: '0.75rem' }">
                {{ blurbButtonText }}
              </span>
            </button>
          </div>

          <!-- Drag order posts -->
          <div class="mt-4">
            <NewsletterPosts :config="postsConfig" />
          </div>

          <!-- Logo -->
          <div class="w-full py-6 flex items-center justify-center">
            <img
              :style="{ width: '125px;' }"
              src="/img/cw-logo.png"
              alt="CareerWise Logo"
            />
          </div>

          <!-- Footer -->
          <div
            class="border-t border-truegray-300 pt-4 flex flex-col items-center justify-center space-y-6"
          >
            <a
              :href="settings.host_domain"
              target="_blank"
              class="no-underline flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                />
              </svg>
            </a>

            <div class="flex items-center justify-center">
              <a
                href="https://creativecommons.org/licenses/by-sa/4.0/"
                target="_blank"
                class="no-underline font-normal flex items-center justify-center space-x-2"
              >
                <img
                  :style="{ width: '80px;' }"
                  src="/img/tenant1/mail/creative-commons.png"
                  alt="creative commons license logo"
                />
                <p class="text-xs text-truegray-300 font-normal">
                  Some rights reserved
                </p>
              </a>
            </div>

            <div
              v-if="config.build"
              class="flex items-center justify-center text-truegray-500 space-x-2"
            >
              <a
                target="_blank"
                href="https://careerwise.school/?terms=true"
                class="font-thin text-xs no-underline font-normal m-0"
              >
                Terms
              </a>
              <p class="m-0 text-xs font-thin">|</p>
              <p class="m-0 text-xs font-thin">Unsubscribe</p>
            </div>

            <div
              v-if="config.build"
              class="flex items-center justify-center text-truegray-500 space-x-2"
            >
              <img
                :style="{ height: '16px' }"
                src="/img/tenant1/mail/hail-icon.png"
                alt="Hail logo"
              />
              <a
                class="no-underline font-thin text-base font-normal"
                href="https://get.hail.to/"
                >Powered by Hail Mail</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import { mapActions, mapGetters } from 'vuex'
import NewsletterListDropdown from './NewsletterListDropdown.vue'
import NewsletterPosts from './NewsletterBuilderPosts.vue'
import * as loadingAnimation from '../../../../../shared/components/loading.json'
import DatePicker from 'vue2-datepicker'
import ProTag from '../../components/ProTag.vue'

export default {
  components: {
    NewsletterListDropdown,
    DatePicker,
    NewsletterPosts,
    ProTag,
  },

  props: {
    config: { default: null },
  },

  data() {
    return {
      loadingAnimationOptions: {
        animationData: loadingAnimation.default,
      },

      loadingAnimate: false,

      newsletterId: null,

      publishWithoutEmail: false,

      newsletterSubject: '',
      newsletterSubjectUpdate: false,

      postsFromNewsletter: null, // for previously sent newsletter display

      newsletterTitle: '',
      newsletterTitleUpdate: false,

      newsletterBlurb: '',
      newsletterBlurbUpdate: false,

      datetimeDefault: null,
      datetimeScheduled: null,

      saveAsDraft: false,

      newsletterPostIds: [],

      queuedMailLists: [],
      queuedSubscriberCount: 0,

      postsPerNewsletter: 30,
      postsModal: false,

      optionsOpen: false,

      submitting: false,

      proTagConfig: {
        small: true,
      },

      toggleClass:
        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none',
      inputWrapperClass:
        'rounded relative bg-white border border-truegray-300 w-full',
      inputLabelClass:
        'absolute transform duration-100 ease-linear px-1 left-3 top-1 z-40 text-truegray-700',
      inputTextClass:
        'flex-grow px-4 m-0 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300',
      inputTextAreaClass:
        'flex-grow px-4 mr-0 outline-none focus:outline-none bg-transparent h-24 font-primary text-black focus:placeholder-truegray-300 transition-all duration-100 ease-in-out w-full',
      ddButtonClass:
        'w-full flex-grow px-4 mr-0 outline-none focus:outline-none bg-transparent h-12 font-primary text-black cursor-pointer',
      ddSvgClass: 'cursor-pointer z-10 h-12 w-5 absolute top-0 flex right-2',
      ddSpanClass: 'block text-base font-primary truncate text-left mt-2',
      ddUlClass:
        'no-scrollbar transform ease-in-out duration-300 max-h-56 px-6 w-full absolute left-0 z-50 bg-white text-base sm:text-sm overflow-auto focus:outline-none rounded-b-xl',
      ddLiClass:
        'w-full cursor-pointer border-b last:border-transparent relative flex items-center px-2 h-12',
      imgWrapperClass:
        'banner-changer w-full h-72 my-2 rounded bg-no-repeat bg-center bg-cover flex items-center justify-center sm relative z-20',
      imgButtonClass:
        'z-30 absolute right-1/2 bottom-1/2 transform translate-y-1/2 translate-x-1/2 btn btn-trans-white text-white m-0 ml-6',
      infoClass:
        'absolute left-full top-0 w-64 z-50 border border-truegray-300 rounded p-4 opacity-0 transition-all duration-300 transform pointer-events-none group-hover:opacity-100 bg-white',
    }
  },

  computed: {
    ...mapGetters({
      posts: 'careerwise/getNewsletterPosts',
      selectedPosts: 'careerwise/getNewsletterSelectedPosts',
      settings: 'shared/getSettings',
      isOverFollowerLimit: 'careerwise/isOverFollowerLimit',
    }),

    isPro() {
      return this.settings.isPro
    },

    submitText() {
      if (this.saveAsDraft) {
        return 'Save'
      }
      if (this.datetimeScheduled) {
        return 'Schedule'
      }
      return 'Send'
    },

    canSubmit() {
      let invalid =
        this.submitting ||
        !this.newsletterSubject ||
        !this.newsletterTitle ||
        !this.selectedPosts.length ||
        (!this.queuedMailLists.length && !this.saveAsDraft) ||
        this.isOverFollowerLimit()
      if (this.publishWithoutEmail) {
        invalid =
          !this.newsletterTitle ||
          !this.selectedPosts.length ||
          this.isOverFollowerLimit()
      }
      return !invalid && !this.optionsOpen
    },

    postsConfig() {
      if (this.config.build) {
        return {
          posts: this.selectedPosts,
          build: true,
        }
      } else {
        return {
          posts: this.postsFromNewsletter,
          build: false,
        }
      }
    },

    dateDisplayed() {
      let date = this.datetimeScheduled || this.datetimeDefault
      let displayed = ''

      if (date) {
        date = new Date(date)
        const options = {
          dateStyle: 'short',
        }

        if (this.datetimeScheduled) {
          // include the time
          options.hour12 = true
          options.timeStyle = 'short'
        }

        return date.toLocaleString([], options)
      }

      return displayed
    },

    postsPromptText() {
      return `Now add some posts${
        !this.newsletterBlurb?.length ? ', or even a blurb' : ''
      }!`
    },

    postsButtonText() {
      if (this.selectedPosts?.length) {
        return 'Edit posts'
      }
      return 'Add posts'
    },

    blurbButtonText() {
      if (this.newsletterBlurb?.length) {
        return 'Edit blurb'
      }
      return 'Add blurb'
    },

    blurbLength() {
      return this.newsletterBlurb?.length || 0
    },

    payload() {
      const payload = {
        // mandatory
        newsletter_title: this.newsletterTitle,
        newsletter_subject: this.newsletterSubject,
        post_ids: this.newsletterPostIds,
        tenant_url: this.settings.host_domain,
        mail_lists: this.queuedMailLists,
        subscriber_count: this.queuedSubscriberCount,
        publish_without_email: this.publishWithoutEmail,
      }

      // Is this a pre-saved newsletter (from draft)?
      if (this.newsletterId) {
        payload.newsletter_id = this.newsletterId
      }

      // optional
      if (this.newsletterBlurb?.length) {
        payload.blurb = this.newsletterBlurb
      }

      if (!!this.datetimeScheduled && this.isPro && !this.saveAsDraft) {
        payload.scheduled_for = this.datetimeScheduled
      }

      if (this.saveAsDraft) {
        payload.is_draft = true
      }

      return payload
    },
  },

  watch: {
    saveAsDraft: {
      handler: function (val) {
        // Don't allow scheduling when saving as draft
        if (val) {
          this.$set(this, 'datetimeScheduled', null)
        }
      },

      deep: true,
    },

    publishWithoutEmail: {
      handler: function (val) {
        // Force email description when not emailing and run resets on fields that can't be used
        if (val) {
          this.$set(this, 'newsletterSubject', 'Published newsletter')
          this.$set(this, 'datetimeScheduled', null)
          this.$set(this, 'queuedMailLists', [])
          this.$set(this, 'queuedSubscriberCount', 0)
          this.$set(this, 'saveAsDraft', false)
        } else {
          this.$set(this, 'newsletterSubject', '')
        }
      },

      deep: true,
    },
  },

  async mounted() {
    // Pre fill the fields with data from model or preview
    if (!this.config.build && this.config.presetData) {
      const data = this.config.presetData
      this.$set(this, 'newsletterTitle', data.newsletterTitle)
      if (data.newsletterBlurb) {
        this.$set(this, 'newsletterBlurb', data.newsletterBlurb)
      }
      this.$set(this, 'postsFromNewsletter', data.newsletterPosts)
    } else {
      this.resetNewsletterFields()
    }

    EventBus.$on('newQueuedMailLists', (selected) => {
      this.$set(this, 'queuedMailLists', selected.selectedIds)
      this.$set(this, 'queuedSubscriberCount', selected.subscriberCount)
    })

    EventBus.$on('newsletterPostOrderUpdated', (postIds) => {
      this.$set(this, 'newsletterPostIds', postIds)
    })

    EventBus.$on('resetNewsletterFields', () => {
      this.resetNewsletterFields()
    })

    EventBus.$on('editDraftNewsletter', (newsletter) => {
      this.resetNewsletterFields()

      const info = newsletter.information
      this.$set(this, 'newsletterId', newsletter.id)
      this.prepareNewsletterPosts(info.post_ids)
      this.$set(this, 'newsletterTitle', newsletter.title || '')
      this.$set(this, 'newsletterSubject', newsletter.subject || '')
      this.$set(this, 'newsletterBlurb', newsletter.blurb || '')
    })

    this.$set(this, 'datetimeDefault', new Date())
  },

  methods: {
    ...mapActions({
      dispatchUpdateSelectedNewsletterPosts:
        'careerwise/dispatchUpdateSelectedNewsletterPosts',
      dispatchGetPostsFromSentNewsletter:
        'careerwise/dispatchGetPostsFromSentNewsletter',
      dispatchSendNewsletter: 'careerwise/dispatchSendNewsletter',
    }),

    init() {},

    resetNewsletterSelectedMailLists() {
      EventBus.$emit('resetNewsletterSelectedMailLists')
    },

    closeSchedule() {
      this.$set(this, 'optionsOpen', false)
    },

    resetSchedule() {
      this.$set(this, 'datetimeScheduled', null)
    },

    /**
     * Disable dates before today and later than 30 days in future
     */
    disabledDate(date) {
      const today = new Date()
      return (
        date < new Date(new Date().setHours(0, 0, 0, 0)) ||
        date > new Date(today.getTime() + 30 * 24 * 3600 * 1000)
      )
    },

    /**
     * Disable times earlier than 4 hours ahead (ignore minutes)
     */
    disabledTime(time) {
      const now = new Date()
      // set earliest time to four hours in future,
      // then round back to previous hour;
      // disabling minutes seems weird to the user otherwise
      // // const fourAhead = now.getTime() + (4*60*60*1000)
      const fourAhead = now.getHours() + 4
      const newDate = new Date(new Date().setHours(fourAhead))
      newDate.setMinutes(0, 0, 0)

      return time < newDate
    },

    beforeMove() {
      return this.config.build
    },

    async onSubmit() {
      this.$set(this, 'submitting', true)

      const response = await this.dispatchSendNewsletter(this.payload)
      if (response) {
        this.resetNewsletterFields()
        EventBus.$emit('refreshPreviousNewsletters')
      }

      this.$set(this, 'submitting', false)
    },

    resetNewsletterFields() {
      this.$set(this, 'newsletterId', null)
      this.$set(this, 'newsletterTitle', '')
      this.$set(this, 'newsletterSubject', '')

      this.$set(this, 'postsFromNewsletter', null)

      this.$set(this, 'newsletterBlurb', '')

      this.$set(this, 'newsletterPostIds', [])

      this.$set(this, 'queuedMailLists', [])
      this.$set(this, 'queuedSubscriberCount', [])

      this.$set(this, 'datetimeScheduled', null)

      this.$set(this, 'saveAsDraft', false)

      this.$set(this, 'publishWithoutEmail', false)

      this.resetNewsletterSelectedMailLists()

      this.dispatchUpdateSelectedNewsletterPosts()
    },

    openPostModal() {
      EventBus.$emit('showModal', {
        action: 'cw-newsletter-posts',
        data: {
          postsPerNewsletter: this.postsPerNewsletter,
        },
      })
    },

    openSendingOptions() {
      const open = this.optionsOpen
      this.$set(this, 'optionsOpen', !open)
    },

    async prepareNewsletterPosts(ids) {
      // Go fetch full post objects for each post id to pass to store
      const response = await this.dispatchGetPostsFromSentNewsletter({
        post_ids: ids,
      })
      this.dispatchUpdateSelectedNewsletterPosts(response.data.posts)
    },

    validateBlurb() {
      if (this.newsletterBlurb.length > 500) {
        const str = this.newsletterBlurb
        const trimmed = str.substring(0, 500)
        this.$set(this, 'newsletterBlurb', trimmed)
      }
    },
  },
}
</script>

<style scoped>
.newsletter-preview * {
  font-family: 'Helvetica';
}

/* Tailwind not working on these column classes for some reason */
.newsletter-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
}

/* Tailwind not working on these column classes for some reason */
.newsletter-grid .col-span-2 {
  grid-column: span 2 / span 2 !important;
}

.newsletter-title {
  font-size: 30px;
  font-weight: 600;
}

.newsletter-body-text {
  font-size: 12px;
  color: #656d78;
  margin: 0;
}

.newsletter-post-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.clamp-subject {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.seperator-right::after {
  content: '';
  position: absolute;
  right: -6px;
  top: 50%;
  height: 14px;
  width: 1px;
  transform: translateY(-50%);
  background: #e5e7eb;
}
</style>
